/**
 * window.__agentVoiceApi — lets an AI agent SPEAK as itself in Whereby calls.
 *
 * How: on install (before any Whereby join) we wrap `navigator.mediaDevices.getUserMedia`
 * so audio requests receive a synthetic microphone track backed by a WebAudio
 * `MediaStreamAudioDestinationNode`. The agent synthesizes speech LOCALLY on its own
 * machine (free, native TTS — no paid cloud TTS):
 *
 *   macOS:   say -o /tmp/utt.wav --data-format=LEI16@22050 "text" && base64 -i /tmp/utt.wav
 *   Windows: PowerShell System.Speech SpeechSynthesizer → .wav → [Convert]::ToBase64String
 *   Linux:   espeak-ng -w /tmp/utt.wav "text" && base64 /tmp/utt.wav
 *
 * …then plays it into the call via `playAudioBase64(base64Wav)`. Other participants hear
 * the agent talk, and Whereby's transcription pipeline processes it like real speech.
 * Utterances queue sequentially.
 *
 * Playwright usage:
 *   const b64 = fs.readFileSync('/tmp/utt.wav').toString('base64')  // in the Node script
 *   await page.evaluate((audio) => window.__agentVoiceApi.playAudioBase64(audio), b64)
 */

export type AgentVoiceApi = {
  /**
   * Plays base64-encoded audio (WAV/MP3/OGG — anything decodeAudioData handles) through
   * the injected mic track. Resolves when playback ends. Utterances queue sequentially.
   */
  playAudioBase64: (base64Audio: string) => Promise<void>
  isSpeaking: () => boolean
}

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export const installAgentVoiceApi = (): AgentVoiceApi => {
  let audioContext: AudioContext | null = null
  let destination: MediaStreamAudioDestinationNode | null = null
  let speaking = false
  let queue: Promise<void> = Promise.resolve()

  const ensureAudioGraph = () => {
    if (!audioContext) {
      audioContext = new AudioContext()
      destination = audioContext.createMediaStreamDestination()
    }
    if (audioContext.state === 'suspended') void audioContext.resume()
    return { audioContext, destination: destination as MediaStreamAudioDestinationNode }
  }

  // Wrap getUserMedia so any audio request (Whereby's useLocalMedia included) gets the
  // synthetic mic. Video portions still go to the real implementation.
  const mediaDevices = navigator.mediaDevices
  const originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices)
  mediaDevices.getUserMedia = async (constraints?: MediaStreamConstraints): Promise<MediaStream> => {
    if (!constraints?.audio) return originalGetUserMedia(constraints)
    const { destination: dest } = ensureAudioGraph()
    const syntheticAudioTrack = dest.stream.getAudioTracks()[0].clone()
    if (!constraints.video) return new MediaStream([syntheticAudioTrack])
    try {
      const videoStream = await originalGetUserMedia({ video: constraints.video })
      return new MediaStream([syntheticAudioTrack, ...videoStream.getVideoTracks()])
    } catch {
      // No camera available (headless agent) — audio-only is fine.
      return new MediaStream([syntheticAudioTrack])
    }
  }

  const playBuffer = (buffer: AudioBuffer): Promise<void> => {
    const { audioContext: ctx, destination: dest } = ensureAudioGraph()
    return new Promise<void>((resolve) => {
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(dest)
      source.onended = () => resolve()
      source.start()
    })
  }

  const api: AgentVoiceApi = {
    playAudioBase64: (base64Audio: string) => {
      const run = async () => {
        speaking = true
        try {
          const { audioContext: ctx } = ensureAudioGraph()
          const audioBuffer = await ctx.decodeAudioData(base64ToArrayBuffer(base64Audio))
          await playBuffer(audioBuffer)
        } finally {
          speaking = false
        }
      }
      queue = queue.then(run, run)
      return queue
    },
    isSpeaking: () => speaking
  }

  const win = window as Window & { __agentVoiceApi?: AgentVoiceApi }
  win.__agentVoiceApi = api
  return api
}
